<template>
  <div class="w-100">
    <!-- Search Input -->
    <div class="d-flex justify-center w-100 mb-4">
      <div class="w-100 position-relative">
        <div
          class="position-absolute d-flex align-center"
          style="left: 20px; top: 50%; transform: translateY(-50%); z-index: 1"
        >
          <SearchIcon :size="24" color="#747474" />
        </div>
        <input
          v-model="internalValue"
          type="text"
          :placeholder="placeholder"
          class="w-100"
          style="
            height: 64px;
            background-color: white;
            border: 1px solid #d0d0d0;
            border-radius: 50px;
            padding: 0 20px 0 50px;
            font-size: 16px;
            font-family: Arial, Helvetica, sans-serif;
            color: #333;
            outline: none;
            transition: border-color 0.3s ease;
          "
          @input="handleInput"
          @focus="handleFocus"
          @blur="handleBlur"
        />
      </div>
    </div>

    <!-- Filter Chips -->
    <div v-if="showFilters" class="d-flex align-center flex-wrap" style="gap: 8px">
      <!-- All Filters (Dynamic Rendering) -->
      <UiDropdown
        v-for="filter in filters"
        :key="filter.id"
        :model-value="getDropdownState(filter.id)"
        @update:model-value="setDropdownState(filter.id, $event)"
        trigger="click"
        placement="bottom-start"
        :compact="true"
        :dropdown-style="{ left: 0 }"
      >
        <template #trigger>
          <!-- Chip Style (Active Filter) -->
          <UiChip
            v-if="filter.variant === 'chip'"
            variant="primary"
            outlined
            rounded
            size="medium"
            :closable="filter.closable !== false"
            @close="handleRemoveFilter(filter)"
            style="cursor: pointer; font-size: 14px"
          >
            <template v-if="filter.icon" #startIcon>
              <component :is="filter.icon" :size="16" :color="filter.iconColor || '#8C1D40'" />
            </template>
            {{ filter.label }}
          </UiChip>

          <!-- Button Style (Available Filter) -->
          <UiButton
            v-else
            variant="dark-outline"
            size="medium"
            :rounded="true"
            style="border-color: #d0d0d0; color: #484848; font-size: 14px; padding: 0 12px 0 16px; margin: 0"
          >
            <template v-if="filter.icon" #startIcon>
              <component
                :is="filter.icon"
                :size="16"
                :color="filter.iconColor || '#484848'"
                style="margin-left: 4px; margin-right: 8px"
              />
            </template>
            {{ filter.label }}
          </UiButton>
        </template>

        <template #default="{ close }">
          <!-- Multi-select with checkboxes -->
          <div v-if="filter.multiSelect" class="multi-select-dropdown">
            <div
              v-for="option in filter.options"
              :key="option.value"
              class="multi-select-item"
              @click="handleMultiSelectToggle(filter, option)"
            >
              <div class="checkbox-container">
                <input
                  type="checkbox"
                  :checked="isOptionSelected(filter, option)"
                  class="multi-select-checkbox"
                  readonly
                />
                <div class="checkbox-custom" />
              </div>
              <span class="option-label">{{ option.label }}</span>
            </div>
          </div>

          <!-- Single select (original behavior) -->
          <template v-else>
            <UiDropdownItem
              v-for="option in filter.options"
              :key="option.value"
              @click="
                handleFilterSelect(filter, option);
                close();
              "
            >
              {{ option.label }}
            </UiDropdownItem>
          </template>
        </template>
      </UiDropdown>

      <!-- Clear All Link -->
      <UiButton
        v-if="hasActiveFilters && showClearAll"
        variant="ghost"
        size="medium"
        style="font-size: 14px; font-weight: 400; margin-left: auto"
        @click="handleClearAll"
      >
        Clear All
      </UiButton>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { SearchIcon } from './icons';
import { Button as UiButton, Chip as UiChip, Dropdown as UiDropdown, DropdownItem as UiDropdownItem } from './ui';

export default defineComponent({
  name: 'SearchBar',

  components: {
    SearchIcon,
    UiButton,
    UiChip,
    UiDropdown,
    UiDropdownItem
  },

  props: {
    /**
     * The current value of the search input
     */
    modelValue: {
      type: String,
      default: ''
    },

    /**
     * Placeholder text for the search input
     */
    placeholder: {
      type: String,
      default: 'Search by keyword'
    },

    /**
     * Whether to show filters below the search input
     */
    showFilters: {
      type: Boolean,
      default: false
    },

    /**
     * Array of all filters - completely dynamic structure
     * Parent component defines everything: icons, labels, state, options
     * Format: [{
     *   id: string,
     *   label: string,
     *   variant: 'chip' | 'button', // determines rendering style
     *   icon?: Component,            // icon component to render
     *   iconColor?: string,          // icon color
     *   closable?: boolean,          // show close button (for chips)
     *   options: [{ value: string, label: string }]
     * }]
     */
    filters: {
      type: Array,
      default: () => []
    },

    /**
     * Whether to show the "Clear All" button when filters are active
     */
    showClearAll: {
      type: Boolean,
      default: true
    }
  },

  emits: [
    'update:modelValue',
    'focus',
    'blur',
    'filter-click',
    'filter-remove',
    'filter-select',
    'multi-select-toggle',
    'clear-all'
  ],

  data() {
    return {
      internalValue: this.modelValue,
      currentOpenDropdown: null
    };
  },

  watch: {
    modelValue(newValue) {
      this.internalValue = newValue;
    }
  },

  computed: {
    hasActiveFilters() {
      return this.filters.some((f) => f.variant === 'chip');
    }
  },

  methods: {
    handleInput(event) {
      this.internalValue = event.target.value;
      this.$emit('update:modelValue', this.internalValue);
    },

    handleFocus(event) {
      this.$emit('focus', event);
    },

    handleBlur(event) {
      this.$emit('blur', event);
    },

    handleFilterClick(filter) {
      this.$emit('filter-click', filter);
    },

    handleRemoveFilter(filter) {
      this.$emit('filter-remove', filter);
    },

    handleFilterSelect(filter, option) {
      this.$emit('filter-select', { filter, option });
    },

    handleClearAll() {
      this.$emit('clear-all');
    },

    handleMultiSelectToggle(filter, option) {
      this.$emit('multi-select-toggle', { filter, option });
    },

    isOptionSelected(filter, option) {
      // Special case for "Any" option - it's selected when no other values are selected
      if (option.value === 'Any') {
        return !filter.selectedValues || filter.selectedValues.length === 0;
      }

      // For other options, check if the value is in the selected values array
      return filter.selectedValues && filter.selectedValues.includes(option.value);
    },

    getDropdownState(filterId) {
      return this.currentOpenDropdown === filterId;
    },

    setDropdownState(filterId, isOpen) {
      if (isOpen) {
        this.currentOpenDropdown = filterId;
      } else if (this.currentOpenDropdown === filterId) {
        this.currentOpenDropdown = null;
      }
    }
  }
});
</script>

<style scoped>
input:hover {
  border-color: #b0b0b0 !important;
}

input:focus {
  border-color: #8c1d40 !important;
}

input::placeholder {
  color: #999;
  font-size: 16px;
}

/* Multi-select dropdown styles */
.multi-select-dropdown {
  padding: 0;
}

.multi-select-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 24px;
  margin: 0 -24px;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
}

.multi-select-item:hover {
  background-color: #d9f1fa;
}

.checkbox-container {
  position: relative;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.multi-select-checkbox {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
}

.checkbox-custom {
  width: 24px;
  height: 24px;
  border: 1px solid #d0d0d0;
  background: white;
  position: relative;
  transition: all 0.15s ease-in-out;
}

.multi-select-checkbox:checked + .checkbox-custom {
  background-color: #8c1d40;
  border-color: #8c1d40;
}

.multi-select-checkbox:checked + .checkbox-custom::after {
  content: '';
  position: absolute;
  left: 7px;
  top: 3px;
  width: 6px;
  height: 12px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.option-label {
  color: #191919;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 16px;
  font-weight: normal;
  line-height: 1.5;
  flex: 1;
}
</style>
